import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Calendar } from '../ui/calendar';
import {
  Plus, ChevronLeft, ChevronRight, Clock, CalendarDays, Users, Filter, X, Zap,
  LayoutGrid, Rows3, MapPin, CalendarRange, Wallet, User, Flag, ExternalLink, StickyNote, CircleDollarSign,
} from 'lucide-react';
import PageHeader from '../ui-pro/PageHeader';
import { DetailPanel, DetailRow } from '../ui-pro/DetailPanel';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  { id: 'nakit', label: 'Nakit', icon: '💵' },
  { id: 'garanti_bbva', label: 'Garanti BBVA', icon: '🏦' },
  { id: 'bostanci', label: 'Bostancı', icon: '🏢' }
];

// Bitiş saati hesapla
const calculateEndTime = (startTime, durationHours) => {
  if (!startTime) return '';
  const [h, m] = startTime.split(':').map(Number);
  const totalMin = h * 60 + m + Math.round(durationHours * 60);
  const eh = Math.floor(totalMin / 60) % 24;
  const em = totalMin % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
};

// Saatler - Sadece 17:00 ve sonrası
const TIME_SLOTS = [
  '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', 
  '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', 
  '23:00', '23:30', '00:00'
];

// Günler
const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const DAYS_FULL_TR = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const CalendarTab = ({
  reservations = [],
  salons = [],
  customers = [],
  referees = [],
  onQuickReservation,
  fetchReservations,
  fetchIncomes,
  organizations = [],
  onNavigate,
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('week'); // day, week
  const [showQuickModal, setShowQuickModal] = useState(false);
  const [filterSalon, setFilterSalon] = useState('all');
  const [filterKind, setFilterKind] = useState('all');
  const [detail, setDetail] = useState(null);
  const [showMobileCalendar, setShowMobileCalendar] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Hızlı rezervasyon form state
  const initQuickForm = {
    date: '',
    start_time: '17:00',
    duration_hours: 1.5,
    salon_id: '',
    customer_id: '',
    payment_method: 'nakit',
    paid_amount: '',
    total_amount: '',
    referee_id: '',
    referee_fee: '',
    notes: ''
  };
  const [quickForm, setQuickForm] = useState(initQuickForm);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerList, setShowCustomerList] = useState(false);

  // Müşteri arama filtresi
  const filteredCustomers = React.useMemo(() => {
    const q = (customerSearch || '').toLowerCase().trim();
    if (!q) return customers.slice(0, 10);
    return customers.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [customers, customerSearch]);

  // Modal açıldığında arama temizle
  React.useEffect(() => {
    if (!showQuickModal) {
      setCustomerSearch('');
      setShowCustomerList(false);
    }
  }, [showQuickModal]);

  // Takvimde görüntülenen ay değişince o ayın rezervasyonlarını backend'den çek
  // (yalnızca güncel ay yüklü olduğundan geçmiş/gelecek aylar boş görünmesin)
  const calMonthStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}`;
  React.useEffect(() => {
    if (fetchReservations) fetchReservations(calMonthStr);
  }, [calMonthStr]);

  // Müşteri adını bul
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Müşteri';
  };

  // Salon adını bul
  const getSalonName = (salonId) => {
    const salon = salons.find(s => s.id === salonId);
    return salon?.name || 'Salon';
  };

  // Hafta günlerini al
  const getWeekDays = () => {
    const days = [];
    const startOfWeek = new Date(selectedDate);
    const dayOfWeek = startOfWeek.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Pazartesi'den başla
    startOfWeek.setDate(startOfWeek.getDate() + diff);
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Yerel tarih -> ISO (UTC kaydırması olmadan)
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Belirli bir saat ve günde rezervasyon var mı?
  const getReservationsForSlot = (date, time, salonId) => {
    const dateStr = toISO(date);
    return reservations.filter(r => {
      if (r.date !== dateStr) return false;
      if (salonId && r.salon_id !== salonId) return false;
      if (filterSalon !== 'all' && r.salon_id !== filterSalon) return false;
      if (!kindMatches(r)) return false;
      
      const slotMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      const startMinutes = parseInt(r.start_time?.split(':')[0] || 0) * 60 + parseInt(r.start_time?.split(':')[1] || 0);
      const endMinutes = parseInt(r.end_time?.split(':')[0] || 0) * 60 + parseInt(r.end_time?.split(':')[1] || 0);
      const adjustedEndMinutes = endMinutes === 0 ? 24 * 60 : endMinutes;
      
      return slotMinutes >= startMinutes && slotMinutes < adjustedEndMinutes;
    });
  };

  // Organizasyon kaynaklı rezervasyon mu?
  const isOrgRes = (r) => Boolean(r.organization_id);
  const getOrgName = (id) => (organizations.find(o => o.id === id)?.name) || 'Organizasyon';
  const resTitle = (r) => (isOrgRes(r) ? getOrgName(r.organization_id) : getCustomerName(r.customer_id));
  const kindMatches = (r) => (filterKind === 'all' ? true : filterKind === 'organization' ? isOrgRes(r) : !isOrgRes(r));

  // Belirli bir günün (ve salon/tür filtresinin) rezervasyonları
  const dayReservations = (date) => {
    const ds = toISO(date);
    return reservations
      .filter(r => r.date === ds && (filterSalon === 'all' || r.salon_id === filterSalon) && kindMatches(r))
      .sort((a, b) => String(a.start_time || '').localeCompare(String(b.start_time || '')));
  };

  // Ay ızgarası (pazartesi başlangıçlı 6 hafta)
  const monthGrid = () => {
    const first = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const start = new Date(first);
    const dow = first.getDay();
    start.setDate(first.getDate() + (dow === 0 ? -6 : 1 - dow));
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  // Salon rengi
  const getSalonColor = (salonId) => {
    const index = salons.findIndex(s => s.id === salonId);
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
      'bg-orange-500', 'bg-pink-500', 'bg-cyan-500'
    ];
    return colors[index % colors.length];
  };

  // Bir önceki/sonraki navigasyon
  const navigate = (direction) => {
    const newDate = new Date(selectedDate);
    if (calendarView === 'day' || calendarView === 'salon') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else {
      newDate.setDate(newDate.getDate() + (direction * 7));
    }
    setSelectedDate(newDate);
  };

  // Slot tıklandığında - boş slot tıklanırsa hızlı rezervasyon modal'ı aç
  const handleSlotClick = (date, time, salonId) => {
    setQuickForm({
      ...initQuickForm,
      date: toISO(date),
      start_time: time,
      salon_id: salonId || (filterSalon !== 'all' ? filterSalon : '')
    });
    setShowQuickModal(true);
  };

  // Hızlı rezervasyon submit - direkt API'ye yaz
  const submitQuickReservation = async () => {
    if (!quickForm.salon_id) { toast.error('Salon seçiniz'); return; }
    if (!quickForm.customer_id) { toast.error('Müşteri seçiniz'); return; }
    if (!quickForm.start_time) { toast.error('Saat seçiniz'); return; }

    setSubmitting(true);
    try {
      const endTime = calculateEndTime(quickForm.start_time, quickForm.duration_hours);
      const payload = {
        salon_id: quickForm.salon_id,
        customer_id: quickForm.customer_id,
        date: quickForm.date,
        start_time: `${quickForm.start_time}:00`,
        end_time: `${endTime}:00`,
        duration_hours: parseFloat(quickForm.duration_hours),
        payment_method: quickForm.payment_method,
        paid_amount: parseFloat(quickForm.paid_amount) || 0,
        total_amount: quickForm.total_amount !== '' ? parseFloat(quickForm.total_amount) : null,
        referee_id: quickForm.referee_id || null,
        referee_fee: quickForm.referee_fee ? parseFloat(quickForm.referee_fee) : null,
        notes: quickForm.notes || ''
      };
      await axios.post(`${API}/reservations`, payload);
      toast.success('Rezervasyon oluşturuldu ✓');
      setShowQuickModal(false);
      setQuickForm(initQuickForm);
      // Verileri yenile
      if (fetchReservations) await fetchReservations(calMonthStr);
      if (fetchIncomes && payload.paid_amount > 0) await fetchIncomes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rezervasyon oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  // Bugün mü kontrolü
  const isToday = (date) => {
    const today = new Date();
    return toISO(date) === toISO(today);
  };

  const weekDays = getWeekDays();

  return (
    <div className="space-y-4">
      <PageHeader
        icon={CalendarDays}
        title="Genel Takvim"
        description="Rezervasyonlar ve organizasyon oturumları · salon bazlı planlama"
        testid="calendar-page-header"
        actions={
          <>
            <Button variant="outline" className="h-9 sm:hidden" onClick={() => setShowMobileCalendar(true)} data-testid="calendar-pick-date-btn">
              <CalendarDays className="w-4 h-4 mr-1" /> Tarih Seç
            </Button>
            <Button
              className="h-9 bg-[#176B91] hover:bg-[#145a7a]"
              onClick={() => { setQuickForm({ ...initQuickForm, date: toISO(selectedDate) }); setShowQuickModal(true); }}
              data-testid="quick-reservation-btn"
            >
              <Zap className="w-4 h-4 mr-1" /> Hızlı Rezervasyon
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          {/* Görünüm seçici */}
          <div className="flex items-center gap-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-1" data-testid="calendar-view-switch">
            {[
              { id: 'day', label: 'Günlük', icon: Rows3 },
              { id: 'week', label: 'Haftalık', icon: CalendarRange },
              { id: 'month', label: 'Aylık', icon: LayoutGrid },
              { id: 'salon', label: 'Salon Bazlı', icon: MapPin },
            ].map(v => (
              <button
                key={v.id}
                onClick={() => setCalendarView(v.id)}
                data-testid={`calendar-view-${v.id}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
                  calendarView === v.id ? 'bg-[#0B172A] text-white' : 'text-slate-600 hover:bg-white'
                }`}
              >
                <v.icon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{v.label}</span>
              </button>
            ))}
          </div>

          {/* Filtreler */}
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <Select value={filterSalon} onValueChange={setFilterSalon}>
              <SelectTrigger className="h-9 w-[168px] text-[13px]" data-testid="calendar-salon-filter"><SelectValue placeholder="Tüm Salonlar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Salonlar</SelectItem>
                {salons.map(salon => <SelectItem key={salon.id} value={salon.id}>{salon.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterKind} onValueChange={setFilterKind}>
              <SelectTrigger className="h-9 w-[150px] text-[13px]" data-testid="calendar-kind-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kayıtlar</SelectItem>
                <SelectItem value="reservation">Rezervasyon</SelectItem>
                <SelectItem value="organization">Organizasyon</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tarih navigasyonu */}
          <div className="ml-auto flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-9 px-2" onClick={() => navigate(-1)} data-testid="calendar-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="px-2 text-center min-w-[190px]">
              <p className="text-[13px] font-semibold text-slate-800" data-testid="calendar-range-label">
                {calendarView === 'month'
                  ? selectedDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
                  : (calendarView === 'day' || calendarView === 'salon')
                    ? selectedDate.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })
                    : `${weekDays[0].toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} – ${weekDays[6].toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`}
              </p>
            </div>
            <Button variant="outline" size="sm" className="h-9 px-2" onClick={() => navigate(1)} data-testid="calendar-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-9 text-[12px] text-[#176B91]" onClick={() => setSelectedDate(new Date())} data-testid="calendar-today">
              Bugün
            </Button>
          </div>
        </div>
      </PageHeader>

      {/* Salon renk kodları */}
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 px-1">
        {salons.map((salon) => (
          <div key={salon.id} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${getSalonColor(salon.id)}`} />
            <span className="text-[11px] text-slate-600">{salon.name}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#0B172A]" />
          <span className="text-[11px] text-slate-600">Organizasyon oturumu</span>
        </div>
      </div>

      {/* ===== HAFTALIK GÖRÜNÜM ===== */}
      {calendarView === 'week' && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid="calendar-week-view">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse min-w-[860px]">
              <thead>
                <tr className="bg-[#F8FAFC]">
                  <th className="w-16 p-2 border-b border-r border-[#E2E8F0] text-[11px] font-semibold text-slate-500 uppercase">Saat</th>
                  {weekDays.map((day, i) => (
                    <th key={i} className={`p-2 border-b border-r border-[#E2E8F0] text-center ${isToday(day) ? 'bg-sky-50' : ''}`}>
                      <div className="text-[11px] text-slate-500">{DAYS_TR[i]}</div>
                      <div className={`text-base font-bold ${isToday(day) ? 'text-[#176B91]' : 'text-slate-800'}`}>{day.getDate()}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TIME_SLOTS.map((time) => (
                  <tr key={time}>
                    <td className="p-2 border-r border-b border-[#E2E8F0] text-[11px] text-slate-500 font-medium bg-[#F8FAFC] align-top">{time}</td>
                    {weekDays.map((day, dayIndex) => {
                      const slotReservations = getReservationsForSlot(day, time);
                      return (
                        <td
                          key={dayIndex}
                          className={`p-1 border-b border-r border-[#E2E8F0] align-top cursor-pointer hover:bg-sky-50/60 transition-colors ${isToday(day) ? 'bg-sky-50/40' : ''}`}
                          onClick={() => slotReservations.length === 0 && handleSlotClick(day, time)}
                        >
                          {slotReservations.map(res => (
                            <button
                              key={res.id}
                              className={`w-full text-left ${isOrgRes(res) ? 'bg-[#0B172A]' : getSalonColor(res.salon_id)} text-white rounded-md px-1.5 py-1 mb-1 text-[11px] hover:opacity-90 transition-opacity`}
                              onClick={(e) => { e.stopPropagation(); setDetail(res); }}
                              data-testid={`calendar-event-${res.id}`}
                            >
                              <div className="font-semibold truncate">{resTitle(res)}</div>
                              <div className="opacity-80 text-[10px] truncate">{getSalonName(res.salon_id)}</div>
                              <div className="opacity-70 text-[10px]">{res.start_time?.slice(0, 5)}-{res.end_time?.slice(0, 5)}</div>
                            </button>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobil: gün seçici + saat listesi */}
          <div className="md:hidden">
            <div className="flex overflow-x-auto p-2 gap-2 bg-[#F8FAFC] border-b border-[#E2E8F0]">
              {weekDays.map((day, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedDate(day)}
                  className={`flex-shrink-0 p-2 rounded-lg text-center min-w-[50px] border ${
                    toISO(selectedDate) === toISO(day)
                      ? 'bg-[#0B172A] text-white border-[#0B172A]'
                      : isToday(day) ? 'bg-sky-50 text-[#176B91] border-sky-200' : 'bg-white text-slate-700 border-[#E2E8F0]'
                  }`}
                >
                  <div className="text-[10px] font-medium">{DAYS_TR[i]}</div>
                  <div className="text-base font-bold">{day.getDate()}</div>
                </button>
              ))}
            </div>
            <div className="divide-y divide-[#E2E8F0]">
              {TIME_SLOTS.map((time) => {
                const slotReservations = getReservationsForSlot(selectedDate, time);
                return (
                  <div key={time} className="flex items-stretch min-h-[56px]" onClick={() => slotReservations.length === 0 && handleSlotClick(selectedDate, time)}>
                    <div className="w-16 p-2 flex items-center justify-center text-[12px] font-medium text-slate-500 bg-[#F8FAFC] border-r border-[#E2E8F0]">{time}</div>
                    <div className="flex-1 p-2">
                      {slotReservations.length > 0 ? slotReservations.map(res => (
                        <button key={res.id} onClick={(e) => { e.stopPropagation(); setDetail(res); }}
                          className={`w-full text-left ${isOrgRes(res) ? 'bg-[#0B172A]' : getSalonColor(res.salon_id)} text-white rounded-lg p-2 mb-1`}
                          data-testid={`calendar-event-${res.id}`}>
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0">
                              <div className="font-semibold text-[13px] truncate">{resTitle(res)}</div>
                              <div className="text-[11px] opacity-80 truncate">{getSalonName(res.salon_id)}</div>
                            </div>
                            <Badge className="text-[10px] bg-white/20 hover:bg-white/20">{res.payment_status === 'odendi' ? 'Ödendi' : 'Bekliyor'}</Badge>
                          </div>
                          <div className="text-[11px] mt-1 opacity-90">
                            {res.start_time?.slice(0, 5)} – {res.end_time?.slice(0, 5)} · ₺{(res.total_amount || 0).toLocaleString('tr-TR')}
                          </div>
                        </button>
                      )) : (
                        <div className="h-full flex items-center justify-center text-slate-300"><Plus className="w-4 h-4" /></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===== GÜNLÜK GÖRÜNÜM ===== */}
      {calendarView === 'day' && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid="calendar-day-view">
          <div className="divide-y divide-[#E2E8F0]">
            {TIME_SLOTS.map((time) => {
              const slotReservations = getReservationsForSlot(selectedDate, time);
              return (
                <div key={time} className="flex items-stretch min-h-[64px] hover:bg-[#F8FAFC] cursor-pointer"
                  onClick={() => slotReservations.length === 0 && handleSlotClick(selectedDate, time)}>
                  <div className="w-20 p-3 flex items-center justify-center text-[13px] font-medium text-slate-500 bg-[#F8FAFC] border-r border-[#E2E8F0]">{time}</div>
                  <div className="flex-1 p-2 flex flex-wrap gap-2">
                    {slotReservations.length > 0 ? slotReservations.map(res => (
                      <button key={res.id} onClick={(e) => { e.stopPropagation(); setDetail(res); }}
                        className={`${isOrgRes(res) ? 'bg-[#0B172A]' : getSalonColor(res.salon_id)} text-white rounded-lg p-2.5 flex-1 min-w-[210px] max-w-[320px] text-left hover:opacity-95`}
                        data-testid={`calendar-event-${res.id}`}>
                        <div className="flex justify-between items-start gap-2">
                          <div className="font-semibold text-[13px] truncate">{resTitle(res)}</div>
                          <Badge className="text-[10px] bg-white/20 hover:bg-white/20 shrink-0">
                            {isOrgRes(res) ? 'ORGANİZASYON' : res.payment_status === 'odendi' ? 'Ödendi' : 'Bekliyor'}
                          </Badge>
                        </div>
                        <div className="text-[12px] opacity-90 mt-0.5">{getSalonName(res.salon_id)}</div>
                        <div className="text-[11px] mt-1.5 flex justify-between opacity-90">
                          <span>{res.start_time?.slice(0, 5)} – {res.end_time?.slice(0, 5)}</span>
                          <span className="font-semibold">₺{(res.total_amount || 0).toLocaleString('tr-TR')}</span>
                        </div>
                      </button>
                    )) : (
                      <div className="flex-1 flex items-center justify-center text-slate-300">
                        <Plus className="w-4 h-4 mr-1" /><span className="text-[12px]">Rezervasyon ekle</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== AYLIK GÖRÜNÜM ===== */}
      {calendarView === 'month' && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid="calendar-month-view">
          <div className="grid grid-cols-7 bg-[#F8FAFC] border-b border-[#E2E8F0]">
            {DAYS_TR.map(d => (
              <div key={d} className="px-2 py-2 text-[11px] font-semibold text-slate-500 uppercase text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthGrid().map((day, i) => {
              const rows = dayReservations(day);
              const inMonth = day.getMonth() === selectedDate.getMonth();
              return (
                <div
                  key={i}
                  onClick={() => { setSelectedDate(day); setCalendarView('day'); }}
                  className={`min-h-[96px] border-b border-r border-[#E2E8F0] p-1.5 cursor-pointer transition-colors hover:bg-sky-50/60
                    ${inMonth ? '' : 'bg-[#F8FAFC]/70'} ${isToday(day) ? 'ring-1 ring-inset ring-[#176B91]' : ''}`}
                  data-testid={`calendar-month-day-${toISO(day)}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[12px] font-semibold ${inMonth ? 'text-slate-800' : 'text-slate-400'} ${isToday(day) ? 'text-[#176B91]' : ''}`}>
                      {day.getDate()}
                    </span>
                    {rows.length > 0 && (
                      <span className="text-[10px] font-semibold text-white bg-[#176B91] rounded-full px-1.5">{rows.length}</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {rows.slice(0, 3).map(res => (
                      <button key={res.id} onClick={(e) => { e.stopPropagation(); setDetail(res); }}
                        className={`w-full text-left text-[10px] rounded px-1 py-0.5 truncate text-white ${isOrgRes(res) ? 'bg-[#0B172A]' : getSalonColor(res.salon_id)}`}
                        data-testid={`calendar-event-${res.id}`}>
                        {res.start_time?.slice(0, 5)} {resTitle(res)}
                      </button>
                    ))}
                    {rows.length > 3 && <p className="text-[10px] text-slate-500 pl-1">+{rows.length - 3} kayıt</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== SALON BAZLI GÖRÜNÜM (seçili gün) ===== */}
      {calendarView === 'salon' && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid="calendar-salon-view">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[760px]">
              <thead>
                <tr className="bg-[#F8FAFC]">
                  <th className="w-16 p-2 border-b border-r border-[#E2E8F0] text-[11px] font-semibold text-slate-500 uppercase">Saat</th>
                  {(filterSalon === 'all' ? salons : salons.filter(s => s.id === filterSalon)).map(salon => (
                    <th key={salon.id} className="p-2 border-b border-r border-[#E2E8F0] text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${getSalonColor(salon.id)}`} />
                        <span className="text-[12px] font-semibold text-slate-700 truncate max-w-[150px]">{salon.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TIME_SLOTS.map(time => (
                  <tr key={time}>
                    <td className="p-2 border-r border-b border-[#E2E8F0] text-[11px] text-slate-500 font-medium bg-[#F8FAFC] align-top">{time}</td>
                    {(filterSalon === 'all' ? salons : salons.filter(s => s.id === filterSalon)).map(salon => {
                      const slot = getReservationsForSlot(selectedDate, time, salon.id);
                      return (
                        <td key={salon.id}
                          className="p-1 border-b border-r border-[#E2E8F0] align-top cursor-pointer hover:bg-sky-50/60"
                          onClick={() => slot.length === 0 && handleSlotClick(selectedDate, time, salon.id)}
                          data-testid={`calendar-salon-slot-${salon.id}-${time}`}>
                          {slot.length === 0 ? (
                            <div className="h-7 flex items-center justify-center text-slate-200"><Plus className="w-3 h-3" /></div>
                          ) : slot.map(res => (
                            <button key={res.id} onClick={(e) => { e.stopPropagation(); setDetail(res); }}
                              className={`w-full text-left ${isOrgRes(res) ? 'bg-[#0B172A]' : getSalonColor(res.salon_id)} text-white rounded-md px-1.5 py-1 text-[11px] mb-1`}
                              data-testid={`calendar-event-${res.id}`}>
                              <div className="font-semibold truncate">{resTitle(res)}</div>
                              <div className="opacity-70 text-[10px]">{res.start_time?.slice(0, 5)}-{res.end_time?.slice(0, 5)}</div>
                            </button>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== SAĞ DETAY PANELİ ===== */}
      <DetailPanel
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        testid="reservation-detail-panel"
        icon={detail && isOrgRes(detail) ? CalendarRange : Clock}
        title={detail ? resTitle(detail) : ''}
        subtitle={detail ? `${new Date(detail.date + 'T00:00:00').toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}` : ''}
        footer={detail && (
          <div className="flex flex-wrap gap-2">
            {isOrgRes(detail) ? (
              <Button size="sm" className="h-8 bg-[#0B172A] hover:bg-[#162B49]" onClick={() => { onNavigate?.('organizations'); setDetail(null); }} data-testid="detail-open-organization">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Organizasyonu Aç
              </Button>
            ) : (
              <>
                <Button size="sm" className="h-8 bg-[#176B91] hover:bg-[#145a7a]" onClick={() => { onNavigate?.('reservations'); setDetail(null); }} data-testid="detail-open-reservations">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Rezervasyonlarda Aç
                </Button>
                {detail.customer_id && (
                  <Button size="sm" variant="outline" className="h-8" onClick={() => { onNavigate?.('customers', { customerId: detail.customer_id }); setDetail(null); }} data-testid="detail-open-customer">
                    <User className="w-3.5 h-3.5 mr-1" /> Müşteri Kartı
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      >
        {detail && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              <Badge className={`text-[11px] ${isOrgRes(detail) ? 'bg-[#0B172A] text-white hover:bg-[#0B172A]' : 'bg-sky-100 text-[#176B91] hover:bg-sky-100'}`}>
                {isOrgRes(detail) ? 'ORGANİZASYON OTURUMU' : 'REZERVASYON'}
              </Badge>
              <Badge className={`text-[11px] ${detail.payment_status === 'odendi' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-amber-100 text-amber-800 hover:bg-amber-100'}`}>
                {detail.payment_status === 'odendi' ? 'Ödendi' : 'Ödeme Bekliyor'}
              </Badge>
              {detail.invoice_issued && <Badge className="text-[11px] bg-indigo-100 text-indigo-800 hover:bg-indigo-100">Faturalı</Badge>}
            </div>
            <div className="rounded-lg border border-[#E2E8F0] px-3 py-1">
              <DetailRow icon={Clock} label="Saat" value={`${detail.start_time?.slice(0, 5)} – ${detail.end_time?.slice(0, 5)}`} />
              <DetailRow icon={MapPin} label="Salon" value={getSalonName(detail.salon_id)} />
              <DetailRow icon={User} label={isOrgRes(detail) ? 'Organizasyon' : 'Müşteri'} value={resTitle(detail)} />
              {detail.referee_id && (
                <DetailRow icon={Flag} label="Hakem"
                  value={`${referees.find(r => r.id === detail.referee_id)?.name || 'Hakem'}${detail.referee_fee ? ` · ₺${Number(detail.referee_fee).toLocaleString('tr-TR')}` : ''}`} />
              )}
              <DetailRow icon={CircleDollarSign} label="Toplam Tutar" value={`₺${(detail.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`} accent="text-slate-900" />
              <DetailRow icon={Wallet} label="Tahsil Edilen" value={`₺${(detail.paid_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
                accent={(detail.paid_amount || 0) >= (detail.total_amount || 0) ? 'text-emerald-700' : 'text-amber-700'} />
              {(detail.total_amount || 0) - (detail.paid_amount || 0) > 0.01 && (
                <DetailRow icon={CircleDollarSign} label="Kalan"
                  value={`₺${((detail.total_amount || 0) - (detail.paid_amount || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`} accent="text-rose-700" />
              )}
              {detail.notes && <DetailRow icon={StickyNote} label="Not" value={detail.notes} />}
            </div>
          </div>
        )}
      </DetailPanel>

      {/* Hızlı Rezervasyon Modal - Tam Donanımlı */}
      <Dialog open={showQuickModal} onOpenChange={setShowQuickModal}>
        <DialogContent className="max-w-lg mx-4 max-h-[90vh] overflow-y-auto" data-testid="quick-reservation-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Zap className="w-5 h-5" /> Hızlı Rezervasyon
            </DialogTitle>
            <DialogDescription>Kayıt anında oluşturulur ve listeye eklenir</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Tarih + Saat + Süre */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Tarih *</Label>
                <Input
                  type="date"
                  value={quickForm.date}
                  onChange={(e) => setQuickForm({...quickForm, date: e.target.value})}
                  data-testid="qr-date-input"
                />
              </div>
              <div>
                <Label className="text-xs">Başlangıç *</Label>
                <Select value={quickForm.start_time} onValueChange={(v) => setQuickForm({...quickForm, start_time: v})}>
                  <SelectTrigger data-testid="qr-time-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Süre</Label>
                <Select value={quickForm.duration_hours.toString()} onValueChange={(v) => setQuickForm({...quickForm, duration_hours: parseFloat(v)})}>
                  <SelectTrigger data-testid="qr-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 saat</SelectItem>
                    <SelectItem value="1.5">1.5 saat</SelectItem>
                    <SelectItem value="2">2 saat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {quickForm.start_time && (
              <div className="text-xs text-slate-500 -mt-1 px-1">
                Bitiş: <span className="font-semibold text-slate-700">{calculateEndTime(quickForm.start_time, quickForm.duration_hours)}</span>
              </div>
            )}

            {/* Salon + Müşteri */}
            <div>
              <Label className="text-xs">Salon *</Label>
              <Select value={quickForm.salon_id} onValueChange={(v) => setQuickForm({...quickForm, salon_id: v})}>
                <SelectTrigger data-testid="qr-salon-select"><SelectValue placeholder="Salon seçin" /></SelectTrigger>
                <SelectContent>
                  {salons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="relative">
              <Label className="text-xs">Müşteri *</Label>
              {quickForm.customer_id ? (
                <div className="flex items-center justify-between gap-2 p-2 border-2 border-emerald-500 bg-emerald-50/50 rounded-md" data-testid="qr-customer-selected">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {getCustomerName(quickForm.customer_id).slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{getCustomerName(quickForm.customer_id)}</p>
                      <p className="text-[10px] text-slate-500 truncate">{customers.find(c => c.id === quickForm.customer_id)?.phone || ''}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setQuickForm({...quickForm, customer_id: ''}); setCustomerSearch(''); setShowCustomerList(true); }}
                    className="text-xs text-slate-500 hover:text-rose-600 px-2 py-1 rounded hover:bg-white"
                    data-testid="qr-customer-clear"
                  >
                    Değiştir
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerList(true); }}
                      onFocus={() => setShowCustomerList(true)}
                      placeholder="Müşteri adı, telefon veya e-posta ile ara..."
                      data-testid="qr-customer-search"
                      autoComplete="off"
                    />
                  </div>
                  {showCustomerList && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid="qr-customer-list">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-3 text-xs text-slate-400 text-center">
                          {customerSearch ? 'Eşleşen müşteri bulunamadı' : 'Müşteri yok'}
                        </div>
                      ) : (
                        filteredCustomers.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              setQuickForm({...quickForm, customer_id: c.id});
                              setCustomerSearch('');
                              setShowCustomerList(false);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-0 text-left transition-colors"
                            data-testid={`qr-customer-option-${c.id}`}
                          >
                            <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                              {(c.name || '?').slice(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                              {(c.phone || c.email) && (
                                <p className="text-[10px] text-slate-500 truncate">{c.phone || c.email}</p>
                              )}
                            </div>
                            {typeof c.balance === 'number' && c.balance > 0 && (
                              <span className="text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full font-semibold">
                                ₺{c.balance.toFixed(0)} borç
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Maç Tutarı */}
            <div>
              <Label className="text-xs">Maç Tutarı (₺) <span className="text-slate-400 font-normal">— boş bırakılırsa salon ücretinden hesaplanır</span></Label>
              <Input
                type="number"
                step="0.01"
                value={quickForm.total_amount}
                onChange={(e) => setQuickForm({...quickForm, total_amount: e.target.value})}
                placeholder="Örn: 1500"
                data-testid="qr-total-input"
              />
            </div>

            {/* Hakem (opsiyonel) */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Hakem (opsiyonel)</Label>
                <Select value={quickForm.referee_id || 'none'} onValueChange={(v) => setQuickForm({...quickForm, referee_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="qr-referee-select"><SelectValue placeholder="Hakem yok" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Hakem yok</SelectItem>
                    {referees.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Hakem Ücreti (₺)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={quickForm.referee_fee}
                  onChange={(e) => setQuickForm({...quickForm, referee_fee: e.target.value})}
                  disabled={!quickForm.referee_id}
                  placeholder="0"
                  data-testid="qr-referee-fee-input"
                />
              </div>
            </div>

            {/* Kapora + Ödeme Yöntemi */}
            <div className="bg-emerald-50/50 rounded-lg p-2.5 border border-emerald-200/50 space-y-2">
              <p className="text-xs font-semibold text-emerald-800">Kapora / Ödeme</p>
              <div>
                <Label className="text-xs">Hangi Kasaya?</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-1">
                  {PAYMENT_METHODS.map(pm => (
                    <button
                      key={pm.id}
                      type="button"
                      onClick={() => setQuickForm({...quickForm, payment_method: pm.id})}
                      className={`p-1.5 rounded border-2 text-[11px] font-medium transition-all ${
                        quickForm.payment_method === pm.id
                          ? 'border-emerald-500 bg-white text-emerald-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid={`qr-pm-${pm.id}`}
                    >
                      <span className="text-sm mr-1">{pm.icon}</span>{pm.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Ödenen / Kapora (₺)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={quickForm.paid_amount}
                  onChange={(e) => setQuickForm({...quickForm, paid_amount: e.target.value})}
                  placeholder="0 (ödeme yok)"
                  data-testid="qr-paid-input"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowQuickModal(false)}
                disabled={submitting}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={submitQuickReservation}
                disabled={submitting}
                data-testid="qr-submit-btn"
              >
                {submitting ? 'Kaydediliyor...' : <><Zap className="w-4 h-4 mr-1" /> Rezervasyon Oluştur</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobil Takvim Modal */}
      <Dialog open={showMobileCalendar} onOpenChange={setShowMobileCalendar}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>Tarih Seçin</DialogTitle>
          </DialogHeader>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              if (date) {
                setSelectedDate(date);
                setShowMobileCalendar(false);
              }
            }}
            className="rounded-md border mx-auto"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarTab;
